import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { getOrderDetails, payOrder, deliverOrder } from '../actions/orderActions';
import { ORDER_PAY_RESET, ORDER_DELIVER_RESET } from '../constants/orderConstants';

const OrderScreen = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [sdkReady, setSdkReady] = useState(false);
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [mpesaProcessing, setMpesaProcessing] = useState(false);
  const [mpesaError, setMpesaError] = useState(null);
  const [mpesaSuccess, setMpesaSuccess] = useState(false);
  
  const orderDetails = useSelector((state) => state.orderDetails);
  const { order, loading, error } = orderDetails;
  
  const orderPay = useSelector((state) => state.orderPay);
  const { loading: loadingPay, success: successPay } = orderPay;
  
  const orderDeliver = useSelector((state) => state.orderDeliver);
  const { loading: loadingDeliver, success: successDeliver } = orderDeliver;
  
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  
  // Calculate prices
  if (!loading && order) {
    const addDecimals = (num) => {
      return (Math.round(num * 100) / 100).toFixed(2);
    };

    order.itemsPrice = addDecimals(
      order.orderItems.reduce((acc, item) => acc + item.price * item.qty, 0)
    );
  }

  useEffect(() => {
    const addPayPalScript = async () => {
      const { data: clientId } = await axios.get('/api/config/paypal');
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}`;
      script.async = true;
      script.onload = () => {
        setSdkReady(true);
      };
      document.body.appendChild(script);
    };

    if (!order || successPay || successDeliver || order._id !== id) {
      dispatch({ type: ORDER_PAY_RESET });
      dispatch({ type: ORDER_DELIVER_RESET });
      dispatch(getOrderDetails(id));
    } else if (!order.isPaid) {
      if (order.paymentMethod === 'PayPal' && !window.paypal) {
        addPayPalScript();
      } else {
        setSdkReady(true);
      }
    }
  }, [dispatch, id, successPay, successDeliver, order]);

  const successPaymentHandler = (paymentResult) => {
    dispatch(payOrder(id, paymentResult));
  };

  const deliverHandler = () => {
    dispatch(deliverOrder(order));
  };

  const handleMpesaPayment = async () => {
    // Validate phone number format
    const phoneRegex = /^(?:254|\+254|0)?(7[0-9]{8})$/;
    if (!phoneRegex.test(mpesaPhone)) {
      setMpesaError('Please enter a valid Kenyan phone number');
      return;
    }

    try {
      setMpesaProcessing(true);
      setMpesaError(null);
      
      // Format phone number to required format (254XXXXXXXXX)
      let formattedPhone = mpesaPhone;
      if (mpesaPhone.startsWith('0')) {
        formattedPhone = '254' + mpesaPhone.slice(1);
      } else if (mpesaPhone.startsWith('+254')) {
        formattedPhone = mpesaPhone.slice(1);
      }

      // Call M-Pesa API
      await axios.post(
        `/api/payment/mpesa`,
        {
          orderId: order._id,
          phoneNumber: formattedPhone,
          amount: order.totalPrice,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );

      setMpesaSuccess(true);
      setMpesaProcessing(false);
      // We don't mark the order as paid yet - it will be updated via webhook once M-Pesa confirms payment
      
    } catch (error) {
      setMpesaError(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
      setMpesaProcessing(false);
    }
  };

  return loading ? (
    <Loader />
  ) : error ? (
    <Message variant="danger">{error}</Message>
  ) : (
    <>
      <h1 className="text-2xl font-semibold mb-4">Order {order._id}</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Shipping</h2>
            <p className="mb-2">
              <strong>Name:</strong> {order.user.name}
            </p>
            <p className="mb-2">
              <strong>Email:</strong>{' '}
              <a href={`mailto:${order.user.email}`} className="text-primary">
                {order.user.email}
              </a>
            </p>
            <p className="mb-2">
              <strong>Address:</strong> {order.shippingAddress.address},{' '}
              {order.shippingAddress.city} {order.shippingAddress.postalCode},{' '}
              {order.shippingAddress.country}
            </p>
            {order.shippingAddress.deliveryInstructions && (
              <p className="mb-2">
                <strong>Instructions:</strong> {order.shippingAddress.deliveryInstructions}
              </p>
            )}
            <div className="mt-3">
              {order.isDelivered ? (
                <Message variant="success">
                  Delivered on {new Date(order.deliveredAt).toLocaleDateString()}
                </Message>
              ) : (
                <Message variant="info">Not Delivered</Message>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Payment Method</h2>
            <p className="mb-2">
              <strong>Method:</strong> {order.paymentMethod}
            </p>
            <div className="mt-3">
              {order.isPaid ? (
                <Message variant="success">
                  Paid on {new Date(order.paidAt).toLocaleDateString()}
                </Message>
              ) : (
                <Message variant="warning">Not Paid</Message>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Order Items</h2>
            {order.orderItems.length === 0 ? (
              <Message>Order is empty</Message>
            ) : (
              <div className="divide-y divide-gray-200">
                {order.orderItems.map((item, index) => (
                  <div key={index} className="py-3 flex items-center">
                    <div className="w-16 h-16 flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>
                    <div className="ml-4 flex-1">
                      <Link
                        to={`/product/${item.product}`}
                        className="text-primary hover:underline"
                      >
                        {item.name}
                      </Link>
                    </div>
                    <div className="text-right">
                      {item.qty} x KES {item.price.toLocaleString()} = KES{' '}
                      {(item.qty * item.price).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            <h2 className="text-xl font-semibold mb-4 pb-3 border-b">
              Order Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <div>Items</div>
                <div>KES {order.itemsPrice}</div>
              </div>

              <div className="flex justify-between">
                <div>Shipping</div>
                <div>KES {order.shippingPrice}</div>
              </div>

              <div className="flex justify-between">
                <div>Tax</div>
                <div>KES {order.taxPrice}</div>
              </div>

              <div className="flex justify-between font-bold text-lg pt-3 border-t">
                <div>Total</div>
                <div>KES {order.totalPrice}</div>
              </div>

              {!order.isPaid && order.paymentMethod === 'PayPal' && (
                <div className="mt-4">
                  {loadingPay && <Loader />}
                  {!sdkReady ? (
                    <Loader />
                  ) : (
                    <div>
                      <p className="mb-2 text-sm">Click the button below to pay with PayPal:</p>
                      <button
                        onClick={() => {
                          // This would typically integrate with the PayPal SDK
                          // For now, we'll simulate a successful payment
                          successPaymentHandler({
                            id: Date.now(),
                            status: 'COMPLETED',
                            update_time: new Date().toISOString(),
                            payer: { email_address: userInfo.email }
                          });
                        }}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 rounded-md"
                      >
                        Pay with PayPal
                      </button>
                      <p className="mt-2 text-xs text-gray-500">You will be redirected to PayPal to complete the payment</p>
                    </div>
                  )}
                </div>
              )}

              {!order.isPaid && order.paymentMethod === 'M-Pesa' && (
                <div className="mt-4">
                  {mpesaSuccess ? (
                    <Message variant="success">
                      M-Pesa payment initiated! Check your phone for the STK push and enter your PIN to complete payment.
                    </Message>
                  ) : (
                    <>
                      {mpesaError && <Message variant="danger">{mpesaError}</Message>}
                      <div className="mb-4">
                        <label htmlFor="mpesaPhone" className="block text-gray-700 font-medium mb-2">
                          M-Pesa Phone Number
                        </label>
                        <input
                          type="text"
                          id="mpesaPhone"
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                          placeholder="e.g. 07XXXXXXXX"
                          value={mpesaPhone}
                          onChange={(e) => setMpesaPhone(e.target.value)}
                          disabled={mpesaProcessing}
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Enter the phone number registered with M-Pesa
                        </p>
                      </div>
                      <button
                        type="button"
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 rounded-md"
                        onClick={handleMpesaPayment}
                        disabled={mpesaProcessing}
                      >
                        {mpesaProcessing ? 'Processing...' : 'Pay with M-Pesa'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Bank Transfer Payment Instructions */}
              {!order.isPaid && order.paymentMethod === 'Bank Transfer' && (
                <div className="mt-4 bg-gray-50 p-4 rounded-md">
                  <h3 className="font-medium mb-2">Bank Transfer Instructions</h3>
                  <p className="text-sm mb-2">
                    Please transfer the total amount to our bank account:
                  </p>
                  <div className="text-sm">
                    <p><strong>Bank:</strong> Kenya Commercial Bank (KCB)</p>
                    <p><strong>Account Name:</strong> IKappal East Africa Ltd</p>
                    <p><strong>Account Number:</strong> 1234567890</p>
                    <p><strong>Branch:</strong> Upperhill Branch</p>
                    <p><strong>Reference:</strong> Order #{order._id}</p>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Once you've completed the transfer, please email the payment confirmation to 
                    <a href="mailto:accounts@ikappal.co.ke" className="text-primary"> accounts@ikappal.co.ke</a>
                  </p>
                </div>
              )}

              {userInfo && userInfo.isAdmin && order.isPaid && !order.isDelivered && (
                <div className="mt-4">
                  {loadingDeliver && <Loader />}
                  <button
                    type="button"
                    className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-2 rounded-md"
                    onClick={deliverHandler}
                  >
                    Mark As Delivered
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 text-center">
              <Link to="/profile" className="text-primary hover:underline">
                View All Orders
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderScreen;
